import { type CellContext, flexRender, type Table as TanstackTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  isLoading: boolean;
  isError: boolean;
  /** Message shown in a single full-width row when the query errored. */
  errorMessage: string;
  /** Content shown in a single full-width row when there are zero rows. */
  emptyMessage: ReactNode;
  /** Row click handler; when set, rows get `cursor-pointer`. */
  onRowClick?: (row: TData) => void;
  /** Skeleton rows to render while loading. */
  skeletonRows?: number;
}

// Shared chrome for the server-paginated list tables (employees, customers,
// activities, …). Owns the responsive geometry so every list table behaves
// identically and a layout change is a single edit:
//   - `w-full` fills the container; `minWidth: getTotalSize()` is the floor, so
//     columns scale up on wide screens and the container scrolls only below the
//     sum of the per-column min widths.
//   - `table-fixed` derives widths from the column defs (not cell content), so
//     they stay identical across pages.
//   - body cells override the shadcn default `whitespace-nowrap` (G9: can't edit
//     ui/table.tsx) with `whitespace-normal` so narrow columns wrap instead of
//     forcing the table wider; `align-top` aligns wrapped cells with single-line
//     neighbours.
// Callers own the column defs, query state, filters, and pagination.
export function DataTable<TData>({
  table,
  isLoading,
  isError,
  errorMessage,
  emptyMessage,
  onRowClick,
  skeletonRows = 6,
}: DataTableProps<TData>) {
  const columnCount = table.getVisibleLeafColumns().length;
  const rows = table.getRowModel().rows;

  return (
    <StickyTableScroll>
      <Table className="w-full table-fixed" style={{ minWidth: table.getTotalSize() }}>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => {
                const sortable = h.column.getCanSort();
                const dir = h.column.getIsSorted();
                return (
                  <TableHead key={h.id} style={{ width: h.getSize() }}>
                    {sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 select-none hover:text-foreground"
                        onClick={h.column.getToggleSortingHandler()}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {dir === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : dir === 'desc' ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ArrowUpDown className="size-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      flexRender(h.column.columnDef.header, h.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <TableRow key={`skel-${i}`}>
                {Array.from({ length: columnCount }).map((_c, j) => (
                  <TableCell key={`skel-${i}-${j}`}>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : isError ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="py-10 text-center text-destructive">
                {errorMessage}
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="py-10 text-center text-muted-foreground">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow
                key={r.id}
                className={onRowClick ? 'cursor-pointer' : undefined}
                onClick={onRowClick ? () => onRowClick(r.original) : undefined}
              >
                {r.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className="align-top whitespace-normal"
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </StickyTableScroll>
  );
}

function defaultCell<TData>({ getValue }: CellContext<TData, unknown>) {
  return String(getValue() ?? '');
}
